import { CheckboxField, Form, useFormContext, useDirtyState } from '@calendly/form';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { Link } from '@calendly/ui/components/link';
import { Loader } from '@calendly/ui/components/loader';
import { BodyText } from '@calendly/ui/components/typography';
import { useInterval } from '@calendly/ui/hooks/use-interval';
import classNames from 'classnames';
import { useCallback, useEffect, useState, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SubTitle } from 'features/organization_settings/shared/styles';
import { isGroupMappingEnabledSelector, isUserGroupsSyncingSelector, defaultUserGroupSelector, isDeactivationPopupDisplayedSelector, isPreviewChangesDisplayedSelector, isPreviewInformationLoadingSelector, isUserGroupsLoadingSelector, groupMappingsSelector } from '../selectors';
import { displayDeactivationPopup, hideDeactivationPopup, hidePreviewChangesModal } from '../slice';
import { fetchScimSettings, saveGroupScimConfiguration, fetchPreviewInformation, fetchAvailableAttributeValues, fetchUserGroupsAndGroupMappings, toggleGroupMappingState } from '../thunks';
import { DeactivationPopup } from './deactivation_popup';
import { DefaultGroup } from './default_group';
import { MappingsControls } from './mappings_controls';
import { PreviewChangesModal } from './preview_changes';
import { SwitchField } from './switch_field';
import { validationSchema } from './validation-schema';
const SubmitFormButton = props => {
  const isSubmitting = useSelector(isPreviewInformationLoadingSelector);
  return <Button aria-label={isSubmitting ? I18n.t('publisher.organization_settings.user_provisioning.groups.loading') : null} {...props} loading={isSubmitting} />;
};
const helpMessageContainerCss = "h16atb2g";
const HelpMessageContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(helpMessageContainerCss, props.className)} />;
});
const formContentsContainerCss = "f1w1j09f";
const FormContentsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(formContentsContainerCss, props.className)} />;
});
const styledLoaderCss = "srn49nd";
const StyledLoader = forwardRef((props, ref) => <Loader ref={ref} {...props} className={classNames(styledLoaderCss, props.className)} />);
const ApplyToExistingControl = () => <div>
    <SubTitle>
      {I18n.t('publisher.organization_settings.user_provisioning.groups.apply_to_existing.title')}
    </SubTitle>
    <CheckboxField name="applyToExisting" label={I18n.t('publisher.organization_settings.user_provisioning.groups.apply_to_existing.label')} />
  </div>;
const previewButtonContainerCss = "p1sndfo6";
const PreviewButtonContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(previewButtonContainerCss, props.className)} />;
});
const descriptionContainerCss = "d1yu07t1";
const DescriptionContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(descriptionContainerCss, props.className)} />;
});
const HelpLink = props => <Link {...props} href="https://help.calendly.com/hc/en-us/articles/4403422473751" target="_blank" rel="noopener noreferrer" />;
const syncingLoaderContainerCss = "s1cmtocj";
const SyncingLoaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(syncingLoaderContainerCss, props.className)} />;
});
const syncingLoaderTextBlockCss = "s17j1hfz";
const SyncingLoaderTextBlock = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(syncingLoaderTextBlockCss, props.className)} />;
});
const SyncingLoader = () => <SyncingLoaderContainer>
    <SyncingLoaderTextBlock>
      <BodyText weight="bold">{I18n.t('publisher.organization_settings.user_provisioning.groups.syncing_header')}</BodyText>
    </SyncingLoaderTextBlock>
    <Loader />
    <SyncingLoaderTextBlock>
      <BodyText>{I18n.t('publisher.organization_settings.user_provisioning.groups.syncing_text.line1')}</BodyText>
      <BodyText>{I18n.t('publisher.organization_settings.user_provisioning.groups.syncing_text.line2')}</BodyText>
    </SyncingLoaderTextBlock>
  </SyncingLoaderContainer>;
const FormContents = ({
  isGroupMappingToggledOn,
  setIsGroupMappingToggledOn
}) => {
  const isDirty = useDirtyState();
  const dispatch = useDispatch();
  const isGroupMappingEnabled = useSelector(isGroupMappingEnabledSelector);
  const isPreviewChangesDisplayed = useSelector(isPreviewChangesDisplayedSelector);
  const {
    values: formValues,
    errors
  } = useFormContext();
  const onConfirm = useCallback(() => {
    dispatch(saveGroupScimConfiguration(formValues));
  }, [dispatch, formValues]);
  const onClose = useCallback(() => {
    dispatch(hidePreviewChangesModal());
  }, [dispatch]);
  const onSubmit = useCallback(() => {
    dispatch(fetchPreviewInformation(formValues));
  }, [dispatch, formValues]);
  const onChange = useCallback(() => {
    if (isGroupMappingToggledOn) {
      if (isDirty) {
        dispatch(displayDeactivationPopup());
      } else if (isGroupMappingEnabled) {
        dispatch(toggleGroupMappingState());
        setIsGroupMappingToggledOn(false);
      } else {
        setIsGroupMappingToggledOn(false);
      }
    } else {
      setIsGroupMappingToggledOn(true);
    }
  }, [dispatch, isGroupMappingEnabled, isGroupMappingToggledOn, setIsGroupMappingToggledOn, isDirty]);
  return <>
      <SwitchField isConfigured={isGroupMappingToggledOn} onChange={onChange} />

      <HelpMessageContainer>
        {HtmlParser(I18n.t('publisher.organization_settings.user_provisioning.groups.help_message'), {
        HelpLink
      })}
      </HelpMessageContainer>

      {isGroupMappingToggledOn && <>
          {isPreviewChangesDisplayed && <PreviewChangesModal isOpen={isPreviewChangesDisplayed} onClose={onClose} onConfirm={onConfirm} settingsNewlyToggledOn={isGroupMappingToggledOn && !isGroupMappingEnabled} />}
          <FormContentsContainer>
            <SubTitle>
              {I18n.t('publisher.organization_settings.user_provisioning.groups.mapping')}
            </SubTitle>
            <DescriptionContainer>
              {I18n.t('publisher.organization_settings.user_provisioning.groups.mapping_description')}
            </DescriptionContainer>
            <MappingsControls />
          </FormContentsContainer>
          <DefaultGroup />
          <ApplyToExistingControl />
          <PreviewButtonContainer>
            <SubmitFormButton size="small" onClick={onSubmit} disabled={!!errors.mappings}>
              {I18n.t('publisher.organization_settings.user_provisioning.groups.preview_button')}
            </SubmitFormButton>
          </PreviewButtonContainer>
        </>}
    </>;
};
export const Groups = () => {
  const dispatch = useDispatch();
  const defaultUserGroup = useSelector(defaultUserGroupSelector);
  const isDeactivationPopupDisplayed = useSelector(isDeactivationPopupDisplayedSelector);
  const isUserGroupsLoading = useSelector(isUserGroupsLoadingSelector);
  const groupMappings = useSelector(groupMappingsSelector);
  const isUserGroupsSyncing = useSelector(isUserGroupsSyncingSelector);
  const isGroupMappingEnabled = useSelector(isGroupMappingEnabledSelector);
  const [isGroupMappingToggledOn, setIsGroupMappingToggledOn] = useState(isGroupMappingEnabled);
  const onConfirmDeactivation = useCallback(() => {
    if (isGroupMappingEnabled) {
      dispatch(toggleGroupMappingState());
    }
    dispatch(hideDeactivationPopup());
    setIsGroupMappingToggledOn(false);
  }, [dispatch, isGroupMappingEnabled, setIsGroupMappingToggledOn]);
  const onClose = useCallback(() => {
    dispatch(hideDeactivationPopup());
  }, [dispatch]);
  const SYNC_POLLING_MS = 5000;
  useInterval(() => {
    dispatch(fetchScimSettings());
  }, isUserGroupsSyncing ? SYNC_POLLING_MS : null);
  useEffect(() => {
    dispatch(fetchUserGroupsAndGroupMappings());
    dispatch(fetchAvailableAttributeValues());
  }, [dispatch]);
  const initialValues = {
    applyToExisting: false,
    defaultUserGroupId: defaultUserGroup?.id,
    mappings: groupMappings
  };
  if (isUserGroupsSyncing) {
    return <SyncingLoader />;
  }
  if (isUserGroupsLoading) {
    return <StyledLoader />;
  }
  return <>
      <DeactivationPopup isOpen={isDeactivationPopupDisplayed} onClose={onClose} onConfirm={onConfirmDeactivation} />

      <Form initialValues={initialValues} validationSchema={validationSchema()} validateOnChange>
        <FormContents isGroupMappingToggledOn={isGroupMappingToggledOn} setIsGroupMappingToggledOn={setIsGroupMappingToggledOn} />
      </Form>
    </>;
};

require("./groups.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./groups.js");